import type { CSSProperties, MouseEvent, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  style?: CSSProperties;
}

const PAD: Record<ButtonSize, string> = { sm: '8px 14px', md: '10px 18px', lg: '13px 22px' };
const FONT: Record<ButtonSize, string> = { sm: '14px', md: '16px', lg: '16px' };
const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: { background: 'var(--cta-default)', color: '#fff' },
  secondary: { background: 'var(--surface-tint)', color: 'var(--ink)', border: '1px solid var(--border-default)' },
  ghost: { background: 'transparent', color: 'var(--cta-default)' },
};

export function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  icon = null,
  children,
  onClick,
  style,
}: ButtonProps) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(e) => {
        if (!disabled && variant === 'primary') e.currentTarget.style.background = 'var(--cta-pressed)';
      }}
      onMouseUp={(e) => {
        if (!disabled && variant === 'primary') e.currentTarget.style.background = 'var(--cta-default)';
      }}
      style={{
        fontFamily: 'var(--font-vi)',
        fontWeight: 600,
        fontSize: FONT[size],
        padding: PAD[size],
        borderRadius: 'var(--radius-control)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 'var(--touch-min)',
        opacity: disabled ? 0.45 : 1,
        transition: 'background 0.15s ease, transform 0.05s ease',
        ...VARIANTS[variant],
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
